import { useMutation, useQuery, useLazyQuery } from '@apollo/client';
// @ts-ignore
import nestedProperty from 'nested-property';
import {
  allBillingFrequenciesQuery,
  allCustomInvoiceItems,
  allUserCountriesQuery,
  productCategories as productCategoriesQuery,
  getUsedPartnerCreditsQuery,
} from '../../graphql/queries';
import React, { useContext, useState } from 'react';
import { updateStoreSuperAdminMutation } from '../../graphql/mutations';
import { DateTimeHelpers } from '../../tools/DateTimeHelpers';
import {
  CanAccessPartnerData,
  ViewerContext,
} from '../../providers/ViewerContextProvider';
import { getObjectDiff } from '@/utils/objDiff';
import {
  EditItem,
  EditSection,
  LoadingContainer,
} from '../../components/Layout';
import i18n from '@/utils/i18n';
import { IconButton } from '../../components/Button/Button';
import { FaPlus } from 'react-icons/all';
import { FaMoneyBill } from 'react-icons/fa';
import { centsToPrice } from '@/utils/formatter';
import HelpPopover from '../../components/HelpPopover';
import { InvoiceActions } from '../InvoiceActions/InvoiceActions';
import {
  isValidEmailInput,
  isValidPurchasingOrderEmailInput,
} from '@/utils/email';
import Checkbox from '../../components/Checkbox';
import {
  CheckBoxLabel,
  CheckBoxLabelContainer,
} from '../../components/Checkbox/pickpackCheckbox';
import { useStoreQuery } from '../../graphql/queries/storeAdminInfo.generated';
import { StoreAdminFragmentFragment } from '../../graphql/fragments/storeAdminInfo.generated';
import { Link } from 'react-router-dom';
import { ContentSection } from '../../components/Layout/ContentSection';
import {
  isNullOrWhitespace,
  isValidInvoiceRoutingId,
} from '@/utils/stringHelper';
import { isValidPostalCode, normalizePostalCode } from '@/utils/postal';
import {
  DunningReminderBlockType,
  InvoiceCollectionScope,
} from '../../graphql/generatedTypes';
import { Alert } from 'reactstrap';
import { IsFinance } from '../../providers/ViewerContextProvider';
import { DunningReminderBlockStatus } from '../../components/DunningReminderBlockStatus';

type MerchantInvoicesTabProps = {
  externalPickpackId: string | null;
  title: string;
};

function formatMonthYear(dateString: string | null | undefined): string {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '';
  return date.toLocaleDateString(i18n.language || 'en', {
    month: 'long',
    year: 'numeric',
  });
}

type InvoicingPauseCardProps = {
  isCurrentlyDisabled: boolean;
  disabledPermanently: boolean;
  disabledUntil: string | null;
  canEdit: boolean;
  onTogglePermanent: () => void;
  onChangeUntil: (e: {
    target: {
      name: string;
      value: string;
      hasAttribute: (attr: string) => boolean;
      valueAsDate: Date | null;
    };
  }) => void;
};

function InvoicingPauseCard({
  isCurrentlyDisabled,
  disabledPermanently,
  disabledUntil,
  canEdit,
  onTogglePermanent,
  onChangeUntil,
}: InvoicingPauseCardProps) {
  const statusColor = isCurrentlyDisabled ? 'danger' : 'success';
  const statusTitle = isCurrentlyDisabled
    ? i18n.t('Invoices:invoicingCurrentlyDisabled')
    : i18n.t('Invoices:invoicingCurrentlyActive');
  let statusSubtitle: string | null = null;
  if (isCurrentlyDisabled) {
    if (disabledPermanently) {
      statusSubtitle = i18n.t('Invoices:invoicingPausedPermanently');
    } else if (disabledUntil) {
      statusSubtitle = i18n.t('Invoices:invoicingPausedUntil', {
        month: formatMonthYear(disabledUntil),
      });
    }
  }

  return (
    <>
      <Alert color={statusColor} className="mb-4">
        <strong>{statusTitle}</strong>
        {statusSubtitle && (
          <div className="mt-1" style={{ fontSize: '0.9rem' }}>
            {statusSubtitle}
          </div>
        )}
      </Alert>

      <div className="input-group mb-2">
        <span className="input-group-text" id="invoicing_disabled_until_label">
          {i18n.t('Invoices:invoicingDisabledUntil')}
        </span>
        <input
          type="month"
          data-isdate
          className="form-control"
          name="invoicing_disabled_until"
          onChange={onChangeUntil}
          disabled={!canEdit || disabledPermanently}
          value={
            disabledUntil != null
              ? DateTimeHelpers.toMonthInputString(disabledUntil)
              : ''
          }
          aria-labelledby="invoicing_disabled_until_label"
        />
        <HelpPopover
          text={i18n.t('Invoices:invoicingDisabledUntilHelp')}
          id="invoicing_disabled_until"
        />
        {!canEdit && (
          <span className="text-muted align-self-center ms-2">
            {i18n.t('Invoices:financeOnly')}
          </span>
        )}
      </div>
      <div className="text-muted mb-3" style={{ fontSize: '0.85rem' }}>
        {i18n.t('Invoices:resumeOn')}
      </div>

      <div className="input-group mb-0">
        <CheckBoxLabelContainer
          onClick={() => {
            if (!canEdit) return;
            onTogglePermanent();
          }}
        >
          <Checkbox checked={disabledPermanently} disabled={!canEdit} />
          <CheckBoxLabel>
            {i18n.t('Invoices:invoicingDisabledPermanently')}
          </CheckBoxLabel>
        </CheckBoxLabelContainer>
        <HelpPopover
          text={i18n.t('Invoices:invoicingDisabledPermanentlyHelp')}
          id="invoicing_disabled_permanently"
        />
        {!canEdit && (
          <span className="text-muted ms-2">
            {i18n.t('Invoices:financeOnly')}
          </span>
        )}
      </div>
    </>
  );
}

type StoreAdminWithNewFields = StoreAdminFragmentFragment & {
  invoice_yearly?: boolean | null;
  needs_purchasing_order_number?: boolean | null;
  yearly_renewal_fee?: number | null;
  yearly_renewal_fee_month?: number | null;
  external_id?: string | null;
  regular_payment_target_days?: string | null;
  sepa_2_percent_discount?: boolean | null;
  dunning_reminder_block_type?: DunningReminderBlockType | null;
  dunning_reminder_block_reason?: string | null;
  dunning_reminder_block_until?: string | null;
  inkassokennzeichen?: boolean | null;
};

export const MerchantInvoiceSettingsTab = ({
  externalPickpackId,
  title,
}: MerchantInvoicesTabProps) => {
  const [storeUpdate, setStoreUpdate] =
    useState<StoreAdminWithNewFields | null>(null);
  const [oldStoreUpdate, setOldStoreUpdate] =
    useState<StoreAdminWithNewFields | null>(null);
  const [updateStoreSuperAdmin, { loading: updatingSuperAdminInfo }] =
    useMutation(updateStoreSuperAdminMutation);

  const [
    getUsedPartnerCredits,
    { loading: gettingUsedPartnerCredits, data: getUsedPartnerCreditsData },
  ] = useLazyQuery(getUsedPartnerCreditsQuery);

  async function onGetAlreadyUsedPartnerCredits() {
    await getUsedPartnerCredits({
      variables: { store_id: externalPickpackId },
    });
  }

  const viewer = useContext(ViewerContext);

  const allowedToAccess = CanAccessPartnerData(viewer);

  const { data: countriesData = { __type: { enumValues: [] } } } = useQuery(
    allUserCountriesQuery,
  );
  const countries = countriesData.__type.enumValues.map(
    (country: any) => country.name,
  );

  const { data: customInvoiceTypeData = { __type: { enumValues: [] } } } =
    useQuery(allCustomInvoiceItems);
  const customInvoiceItemTypes: any =
    customInvoiceTypeData.__type.enumValues.map(
      (customInvoiceItemType: any) => (
        <option>{customInvoiceItemType.name}</option>
      ),
    );

  const { data: billingFrequencyData = { __type: { enumValues: [] } } } =
    useQuery(allBillingFrequenciesQuery);
  const billingFrequencies: any = billingFrequencyData.__type.enumValues.map(
    (billingFrequency: any) => <option>{billingFrequency.name}</option>,
  );

  function resetStore(storeIn: any) {
    setStoreUpdate(storeIn);
    setOldStoreUpdate(JSON.parse(JSON.stringify(storeIn)));
  }

  const { data = { store: null }, loading: loadingStore } = useStoreQuery({
    fetchPolicy: 'no-cache',
    skip: !externalPickpackId,
    variables: { id: externalPickpackId || '' },
    onCompleted: (storeData) => {
      // @ts-ignore
      if (storeData) {
        resetStore(storeData.store);
      }
    },
  });

  if (loadingStore || !storeUpdate) {
    return <LoadingContainer />;
  }

  const store: any = data.store;

  // Number(), because no input in this form carries data-isnumber: every
  // numeric field arrives as a number from the query and a string once typed.
  const yearlyRenewalFeeCents = Number(storeUpdate.yearly_renewal_fee);
  const canPickYearlyRenewalFeeMonth =
    Number.isFinite(yearlyRenewalFeeCents) && yearlyRenewalFeeCents > 0;

  // @ts-ignore
  function handleChange({ target }) {
    const { name, value } = target;
    const isNumber = target.hasAttribute('data-isnumber');
    const isDate = target.hasAttribute('data-isdate');
    let formattedValue = value;
    if (isNumber) formattedValue = Number(value);
    if (isDate) {
      const date = (target as EventTarget & HTMLInputElement).valueAsDate;
      if (date != null) {
        formattedValue = DateTimeHelpers.toDateTimeOffsetString(date);
      } else {
        formattedValue = null;
      }
    }

    setStoreUpdate((oldStoreUpdate: any) => {
      let newStoreUpdate = { ...oldStoreUpdate };
      nestedProperty.set(newStoreUpdate, name, formattedValue);

      // Handle mutual exclusivity for fee fields when circulation-based billing is not enabled
      if (
        !newStoreUpdate.use_circulation_based_billing &&
        formattedValue &&
        formattedValue !== 0
      ) {
        const feeFields = [
          'minimum_monthly_transaction_charge',
          'monthly_service_fee',
          'monthly_flat_fee',
        ];

        if (feeFields.includes(name)) {
          // Clear other fee fields when one is set to a non-zero value
          feeFields.forEach((field) => {
            if (field !== name) {
              newStoreUpdate[field] = null;
            }
          });
        }
      }

      return newStoreUpdate;
    });
  }

  /*
   * Returns 1 if the IBAN is valid
   * Returns FALSE if the IBAN's length is not as should be (for CY the IBAN Should be 28 chars long starting with CY )
   * Returns any other number (checksum) when the IBAN is invalid (check digits do not match)
   */
  function isValidIBANNumber(iban?: string | null) {
    // not every store needs to have an iban
    if (iban == null || iban.trim() === '') {
      return true;
    }

    var CODE_LENGTHS = {
      AD: 24,
      AE: 23,
      AT: 20,
      AZ: 28,
      BA: 20,
      BE: 16,
      BG: 22,
      BH: 22,
      BR: 29,
      CH: 21,
      CR: 22,
      CY: 28,
      CZ: 24,
      DE: 22,
      DK: 18,
      DO: 28,
      EE: 20,
      ES: 24,
      FI: 18,
      FO: 18,
      FR: 27,
      GB: 22,
      GI: 23,
      GL: 18,
      GR: 27,
      GT: 28,
      HR: 21,
      HU: 28,
      IE: 22,
      IL: 23,
      IS: 26,
      IT: 27,
      JO: 30,
      KW: 30,
      KZ: 20,
      LB: 28,
      LI: 21,
      LT: 20,
      LU: 20,
      LV: 21,
      MC: 27,
      MD: 24,
      ME: 22,
      MK: 19,
      MR: 27,
      MT: 31,
      MU: 30,
      NL: 18,
      NO: 15,
      PK: 24,
      PL: 28,
      PS: 29,
      PT: 25,
      QA: 29,
      RO: 24,
      RS: 22,
      SA: 24,
      SE: 24,
      SI: 19,
      SK: 24,
      SM: 27,
      TN: 24,
      TR: 26,
      AL: 28,
      BY: 28,
      EG: 29,
      GE: 22,
      IQ: 23,
      LC: 32,
      SC: 31,
      ST: 25,
      SV: 28,
      TL: 23,
      UA: 29,
      VA: 22,
      VG: 24,
      XK: 20,
    };
    iban = String(iban)
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, ''); // keep only alphanumeric characters
    const code = iban.match(/^([A-Z]{2})(\d{2})([A-Z\d]+)$/); // match and capture (1) the country code, (2) the check digits, and (3) the rest
    // check syntax and length
    if (!code || iban.length !== (CODE_LENGTHS as any)[code[1]]) {
      return false;
    }
    // rearrange country code and check digits, and convert chars to ints
    const digits = (code[3] + code[1] + code[2]).replace(
      /[A-Z]/g,
      function (letter) {
        return (letter.charCodeAt(0) - 55).toString();
      },
    );
    // final check
    return mod97(digits);
  }

  function mod97(digits: string) {
    var checksum: number = parseInt(digits.slice(0, 2));
    var fragment: string;
    for (var offset = 2; offset < digits.length; offset += 7) {
      fragment = String(checksum) + digits.substring(offset, offset + 7);
      checksum = parseInt(fragment, 10) % 97;
    }
    console.log(checksum);
    return checksum === 1;
  }

  function handleSaveSuperAdmin() {
    if (storeUpdate == null) {
      return;
    }
    if (!isValidIBANNumber(storeUpdate.invoice_iban)) {
      alert(`${i18n.t('Invoices:invalidIban')}: ${storeUpdate.invoice_iban}`);
      return;
    }
    // Validate dunning reminder block reason if type is PERMANENTLY
    if (
      storeUpdate.dunning_reminder_block_type ===
        DunningReminderBlockType.Permanently &&
      !storeUpdate.dunning_reminder_block_reason?.trim()
    ) {
      alert(i18n.t('Invoices:dunningReminderBlockReasonRequired'));
      return;
    }
    const getMutationInput = (storeIn: any) => ({
      seller_id: storeIn.seller_id,
      order_number: storeIn.order_number,
      invoice_routing_id: storeIn.invoice_routing_id,
      invoice_headline: storeIn.invoice_headline,
      onboarding_fee_vytal: isNaN(parseInt(storeIn.onboarding_fee_vytal))
        ? null
        : parseInt(storeIn.onboarding_fee_vytal),
      minimum_monthly_transaction_charge: isNaN(
        parseInt(storeIn.minimum_monthly_transaction_charge),
      )
        ? null
        : parseInt(storeIn.minimum_monthly_transaction_charge),
      monthly_service_fee: isNaN(parseInt(storeIn.monthly_service_fee))
        ? null
        : parseInt(storeIn.monthly_service_fee),
      monthly_flat_fee: isNaN(parseInt(storeIn.monthly_flat_fee))
        ? null
        : parseInt(storeIn.monthly_flat_fee),
      use_circulation_based_billing:
        storeIn.use_circulation_based_billing || false,
      invoice_email: (storeIn.invoice_email || '').trim() || null,
      // Dedicated purchasing-order contacts; deliberately not derived from
      // invoice_email. Emptying the field clears it.
      purchasing_order_email:
        (storeIn.purchasing_order_email || '').trim() || null,
      legal_name: (storeIn.legal_name || '').trim() || null,
      invoice_address: {
        ...(storeIn.invoice_address || {}),
        __typename: undefined,
      },
      custom_invoice_items: storeIn.custom_invoice_items
        ? storeIn.custom_invoice_items.map((item: object) => ({
            ...item,
            __typename: undefined,
          }))
        : null,
      contracted_date: storeIn.contracted_date,
      onboarded_date: storeIn.onboarded_date,
      test_period_end: storeIn.test_period_end,
      coming_soon: storeIn.coming_soon,
      published: storeIn.published,
      can_preorder: storeIn.can_preorder,
      can_deliver: storeIn.can_deliver,
      always_online: storeIn.always_online,
      phone_from_vytal: storeIn.phone_from_vytal,
      sim_info: storeIn.sim_info,
      sim_phone_number: storeIn.sim_phone_number,
      sim_payment_automation: storeIn.sim_payment_automation,
      show_in_stats: storeIn.show_in_stats,
      region: parseInt(storeIn.region),
      invoice_email_subject_prefix: storeIn.invoice_email_subject_prefix,
      invoice_iban_name: storeIn.invoice_iban_name,
      invoice_iban: storeIn.invoice_iban?.replaceAll(/\s/g, ''),
      partner_credits: Number(storeIn.partner_credits) || 0,
      partner_credits_usable_until: storeIn.partner_credits_usable_until || '',
      disable_automatic_partner_invoice_sending:
        storeIn.disable_automatic_partner_invoice_sending,
      disable_automatic_partner_charging:
        storeIn.disable_automatic_partner_charging,
      charge_onboarding_fee: storeIn.charge_onboarding_fee,
      vat_number: storeIn.vat_number,
      // Newly added fields
      external_id: (storeIn.external_id || '').trim() || null,
      invoice_yearly: storeIn.invoice_yearly || false,
      needs_purchasing_order_number:
        storeIn.needs_purchasing_order_number || false,
      yearly_renewal_fee: isNaN(parseInt(storeIn.yearly_renewal_fee))
        ? null
        : parseInt(storeIn.yearly_renewal_fee),
      yearly_renewal_fee_month: isNaN(
        parseInt(storeIn.yearly_renewal_fee_month),
      )
        ? null
        : parseInt(storeIn.yearly_renewal_fee_month),
      regular_payment_target_days:
        storeIn.regular_payment_target_days &&
        storeIn.regular_payment_target_days !== ''
          ? Number(storeIn.regular_payment_target_days)
          : null,
      sepa_2_percent_discount: storeIn.sepa_2_percent_discount || false,
      dunning_reminder_block_type: storeIn.dunning_reminder_block_type
        ? storeIn.dunning_reminder_block_type
        : null,
      dunning_reminder_block_reason:
        storeIn.dunning_reminder_block_reason?.trim() || null,
      inkassokennzeichen: storeIn.inkassokennzeichen || false,
      invoicing_disabled_permanently:
        storeIn.invoicing_disabled_permanently || false,
      invoicing_disabled_until: storeIn.invoicing_disabled_until || null,
    });
    const mutationInput = getObjectDiff(
      getMutationInput(oldStoreUpdate),
      getMutationInput(storeUpdate),
    );
    console.log('mutationInput admin', mutationInput);

    updateStoreSuperAdmin({
      variables: {
        id: store.id,
        store: mutationInput,
      },
      refetchQueries: [
        {
          query: productCategoriesQuery,
          variables: {
            id: store.id,
            filter: 'ALL',
          },
        },
      ],
      update: (cache, { data: { updateStoreSuperAdmin: updatedStore } }) => {
        resetStore(updatedStore);
      },
    }).catch((err: any) => {
      alert(i18n.t('Invoices:errorUpdatingStore') + ': ' + err);
    });
  }

  console.log({ country: storeUpdate.invoice_address?.country });

  const addNewCustomInvoiceItem = () => {
    setStoreUpdate((oldStoreUpdate: any) => {
      const newCustomInvoiceItems = [
        ...(oldStoreUpdate.custom_invoice_items || []),
        {
          title: '',
          description: '',
          total: 0,
          type: 'DEFAULT',
          billing_start_with_onboarding: true,
          billing_frequency: 'MONTHLY',
        },
      ];
      return {
        ...oldStoreUpdate,
        custom_invoice_items: newCustomInvoiceItems,
      };
    });
  };

  const updateCustomInvoiceItem = (
    index: number,
    invoiceItemUpdate: object,
  ) => {
    setStoreUpdate((oldStoreUpdate: any) => {
      const newCustomInvoiceItems = [
        ...(oldStoreUpdate.custom_invoice_items || []),
      ];

      newCustomInvoiceItems[index] = {
        ...newCustomInvoiceItems[index],
        ...invoiceItemUpdate,
      };

      console.log(newCustomInvoiceItems[index]);

      return {
        ...oldStoreUpdate,
        custom_invoice_items: newCustomInvoiceItems,
      };
    });
  };

  const deleteCustomInvoiceItem = (removeAtIndex: number) => {
    setStoreUpdate((oldStoreUpdate: any) => {
      const newCustomInvoiceItems = [
        ...(oldStoreUpdate.custom_invoice_items || []),
      ];

      newCustomInvoiceItems.splice(removeAtIndex, 1);

      return {
        ...oldStoreUpdate,
        custom_invoice_items: newCustomInvoiceItems,
      };
    });
  };

  if (!externalPickpackId)
    return <p>{i18n.t('Invoices:externalIdNotAvailable')}</p>;

  if (!store)
    return (
      <p>
        {i18n.t('Invoices:storeCouldNotBeLoaded')}: {externalPickpackId}
      </p>
    );

  if (!allowedToAccess) {
    return null;
  }

  // @ts-ignore
  return (
    <ContentSection title={title} collapsable={false} key="content">
      <EditSection
        title={i18n.t('Invoices:invoices')}
        isLoading={updatingSuperAdminInfo}
        onSave={handleSaveSuperAdmin}
      >
        <EditItem title={i18n.t('Invoices:basicInvoiceInformation')}>
          {storeUpdate.invoice_collection &&
            storeUpdate.invoice_collection.scope ===
              InvoiceCollectionScope.PenaltyBillsOnly && (
              <div className={'input-group mb-3'}>
                <Link
                  to={`/invoice-collection/${storeUpdate.invoice_collection.id}`}
                >
                  {i18n.t('InvoiceCollection:storePenaltyBillsViaCollection', {
                    name: storeUpdate.invoice_collection.name,
                  })}
                </Link>
              </div>
            )}
          {storeUpdate.invoice_collection &&
            storeUpdate.invoice_collection.scope !==
              InvoiceCollectionScope.PenaltyBillsOnly && (
              <div className={'input-group mb-3'}>
                <Link
                  to={`/invoice-collection/${storeUpdate.invoice_collection.id}`}
                >
                  ⚠️ This store is used in the invoice collection:{' '}
                  {storeUpdate.invoice_collection.name}
                </Link>{' '}
                The store invoice information will not be used.
              </div>
            )}
          <div className={'input-group mb-3'}>
            <span className="input-group-text" id="basic-addon1">
              {i18n.t('Invoices:invoiceRoutingId')}
            </span>
            <input
              type="text"
              className={
                'form-control' +
                (isValidInvoiceRoutingId(storeUpdate.invoice_routing_id)
                  ? ''
                  : ' is-invalid')
              }
              name="invoice_routing_id"
              onChange={handleChange}
              value={storeUpdate.invoice_routing_id || ''}
              placeholder={i18n.t('Invoices:routingIdPlaceholder')}
            />
          </div>
          <div className={'input-group mb-3'}>
            <span className="input-group-text" id="basic-addon1">
              {i18n.t('Invoices:additionalInvoiceHeadline')}
            </span>
            <input
              type="text"
              className="form-control"
              name="invoice_headline"
              onChange={handleChange}
              value={storeUpdate.invoice_headline || ''}
            />
          </div>
          <div className={'input-group mb-3'}>
            <span className="input-group-text" id="basic-addon1">
              {i18n.t('Invoices:orderNumber')}
            </span>
            <input
              type="text"
              className="form-control"
              name="order_number"
              onChange={handleChange}
              value={storeUpdate.order_number || ''}
            />
          </div>
          <div className={'input-group mb-3'}>
            <span className="input-group-text" id="basic-addon1">
              {i18n.t('Invoices:sellerId')}
            </span>
            <input
              type="text"
              className="form-control"
              name="seller_id"
              onChange={handleChange}
              value={storeUpdate.seller_id || ''}
            />
          </div>
          <div className="input-group mb-3">
            <span className="input-group-text" id="basic-addon1">
              {i18n.t('Invoices:debitorAccountNumber')}
            </span>
            <input
              type="text"
              className={'form-control'}
              name="debitor_account_number"
              autoComplete="off"
              disabled
              lang="en-US"
              value={storeUpdate.debitor_account_number || ''}
              placeholder={''}
            />
          </div>
          <div className="input-group mb-3">
            <span className="input-group-text" id="basic-addon1">
              {i18n.t('Invoices:vatNumber')}
            </span>
            <input
              type="text"
              className={'form-control'}
              name="vat_number"
              autoComplete="off"
              lang="en-US"
              onChange={(e) => {
                handleChange(e);
              }}
              value={storeUpdate.vat_number || ''}
              placeholder={''}
            />
          </div>
          <div className="input-group mb-3">
            <CheckBoxLabelContainer
              onClick={() =>
                setStoreUpdate({
                  ...storeUpdate,
                  invoice_yearly: !storeUpdate.invoice_yearly,
                })
              }
            >
              <Checkbox checked={!!storeUpdate.invoice_yearly}></Checkbox>
              <CheckBoxLabel>{i18n.t('Invoices:invoiceYearly')}</CheckBoxLabel>
            </CheckBoxLabelContainer>
            <HelpPopover
              text={i18n.t('Invoices:invoiceYearlyHelp')}
              id="invoice_yearly"
            />
          </div>
        </EditItem>

        <EditItem title={i18n.t('Invoices:billingFeeSettings')}>
          <div className="input-group mb-3">
            <span className="input-group-text" id="basic-addon1">
              {i18n.t('Invoices:vytalOnboardingFee')}
            </span>
            <input
              type="number"
              className={'form-control'}
              name="onboarding_fee_vytal"
              autoComplete="off"
              lang="en-US"
              onChange={handleChange}
              step="1"
              value={storeUpdate.onboarding_fee_vytal || ''}
              placeholder={''}
            />
            <span className="input-group-text" id="basic-addon1">
              {centsToPrice(
                storeUpdate.onboarding_fee_vytal,
                storeUpdate.currency,
              )}
            </span>
            <HelpPopover
              text={i18n.t('Invoices:vytalOnboardingFeeHelp')}
              id="onboarding_fee_vytal"
            />
          </div>
          <div className="input-group mb-3">
            <CheckBoxLabelContainer
              onClick={() => {
                const newValue = !storeUpdate.use_circulation_based_billing;
                setStoreUpdate((prevState: any) => {
                  const newState = {
                    ...prevState,
                    use_circulation_based_billing: newValue,
                  };

                  // If circulation-based billing is enabled, clear monthly service fee and flat fee
                  if (newValue) {
                    newState.monthly_service_fee = null;
                    newState.monthly_flat_fee = null;
                  }

                  return newState;
                });
              }}
            >
              <Checkbox
                checked={!!storeUpdate.use_circulation_based_billing}
              ></Checkbox>
              <CheckBoxLabel>
                {i18n.t('Invoices:useCirculationBasedBilling')}
              </CheckBoxLabel>
            </CheckBoxLabelContainer>
            <HelpPopover
              text={i18n.t('Invoices:useCirculationBasedBillingHelp')}
              id="use_circulation_based_billing"
            />
          </div>
          <div className="input-group mb-3">
            <span className="input-group-text" id="basic-addon1">
              {i18n.t('Invoices:minimumUsageFee')}
            </span>
            <input
              type="number"
              className={'form-control'}
              name="minimum_monthly_transaction_charge"
              autoComplete="off"
              lang="en-US"
              onChange={handleChange}
              step="1"
              value={storeUpdate.minimum_monthly_transaction_charge || ''}
              placeholder={''}
            />
            <span className="input-group-text" id="basic-addon1">
              {centsToPrice(
                storeUpdate.minimum_monthly_transaction_charge,
                storeUpdate.currency,
              )}
            </span>
            <HelpPopover
              text={i18n.t('Invoices:minimumUsageFeeHelp')}
              id="minimum_monthly_transaction_charge"
            />
          </div>
          <div className="input-group mb-3">
            <span className="input-group-text" id="basic-addon1">
              {i18n.t('Invoices:flatFee')}
            </span>
            <input
              type="number"
              className={'form-control'}
              name="monthly_flat_fee"
              autoComplete="off"
              lang="en-US"
              onChange={handleChange}
              step="1"
              value={storeUpdate.monthly_flat_fee || ''}
              placeholder={''}
              disabled={!!storeUpdate.use_circulation_based_billing}
            />
            <span className="input-group-text" id="basic-addon1">
              {centsToPrice(storeUpdate.monthly_flat_fee, storeUpdate.currency)}
            </span>
            <HelpPopover
              text={i18n.t('Invoices:flatFeeHelp')}
              id="monthly_flat_fee"
            />
          </div>
          <div className="input-group mb-3">
            <span className="input-group-text" id="basic-addon1">
              {i18n.t('Invoices:serviceFee')}
            </span>
            <input
              type="number"
              className={'form-control'}
              name="monthly_service_fee"
              autoComplete="off"
              lang="en-US"
              onChange={handleChange}
              step="1"
              value={storeUpdate.monthly_service_fee || ''}
              placeholder={''}
              disabled={!!storeUpdate.use_circulation_based_billing}
            />
            <span className="input-group-text" id="basic-addon1">
              {centsToPrice(
                storeUpdate.monthly_service_fee,
                storeUpdate.currency,
              )}
            </span>
            <HelpPopover
              text={i18n.t('Invoices:serviceFeeHelp')}
              id="monthly_service_fee"
            />
          </div>
          <div className="input-group mb-3">
            <span className="input-group-text" id="basic-addon1">
              {i18n.t('Invoices:yearlyRenewalFee')}
            </span>
            <input
              type="number"
              className={'form-control'}
              name="yearly_renewal_fee"
              autoComplete="off"
              lang="en-US"
              onChange={handleChange}
              step="1"
              value={storeUpdate.yearly_renewal_fee || ''}
              placeholder={''}
            />
            <span className="input-group-text" id="basic-addon1">
              {centsToPrice(
                storeUpdate.yearly_renewal_fee,
                storeUpdate.currency,
              )}
            </span>
            <HelpPopover
              text={i18n.t('Invoices:yearlyRenewalFeeHelp')}
              id="yearly_renewal_fee"
            />
          </div>
          <div className="input-group mb-3">
            <span className="input-group-text" id="basic-addon1">
              {i18n.t('Invoices:yearlyRenewalFeeMonth')}
            </span>
            <select
              className="form-control"
              name="yearly_renewal_fee_month"
              autoComplete="off"
              onChange={handleChange}
              value={storeUpdate.yearly_renewal_fee_month ?? ''}
              disabled={!canPickYearlyRenewalFeeMonth}
            >
              <option value="">
                {i18n.t('Invoices:yearlyRenewalFeeMonthOnboarding')}
              </option>
              {Array.from({ length: 12 }, (_, index) => index + 1).map(
                (month) => (
                  <option key={month} value={month}>
                    {i18n.t(`Common:Month:${month}`) as string}
                  </option>
                ),
              )}
            </select>
            <HelpPopover
              text={i18n.t('Invoices:yearlyRenewalFeeMonthHelp')}
              id="yearly_renewal_fee_month"
            />
          </div>
          <div className="input-group mb-3">
            <CheckBoxLabelContainer
              onClick={() =>
                setStoreUpdate({
                  ...storeUpdate,
                  charge_onboarding_fee:
                    storeUpdate.charge_onboarding_fee === false,
                })
              }
            >
              <Checkbox
                checked={storeUpdate.charge_onboarding_fee !== false}
              ></Checkbox>
              <CheckBoxLabel>
                {i18n.t('Invoices:addOnboardingFeeToFirstInvoice')}
              </CheckBoxLabel>
            </CheckBoxLabelContainer>
            <HelpPopover
              text={i18n.t('Invoices:chargeOnboardingFeeHelp')}
              id="charge_onboarding_fee"
            />
          </div>
        </EditItem>

        <EditItem title={i18n.t('Invoices:paymentSettingsSection')}>
          <div className="input-group mb-3">
            <span className="input-group-text" id="basic-addon1">
              {i18n.t('Invoices:regularPaymentTargetDays')}
            </span>
            <input
              type="text"
              className={'form-control'}
              name="regular_payment_target_days"
              autoComplete="off"
              lang="en-US"
              onChange={(e) => {
                const value = e.target.value;
                // Strip all non-numeric characters
                const numericValue = value.replace(/\D/g, '');
                setStoreUpdate({
                  ...storeUpdate,
                  regular_payment_target_days: numericValue as any,
                });
              }}
              value={String(storeUpdate.regular_payment_target_days || '')}
              placeholder="14"
            />
            <HelpPopover
              text={i18n.t('Invoices:regularPaymentTargetDaysHelp')}
              id="regular_payment_target_days"
            />
          </div>

          <div className="input-group mb-3">
            <CheckBoxLabelContainer
              onClick={() =>
                setStoreUpdate({
                  ...storeUpdate,
                  sepa_2_percent_discount: !storeUpdate.sepa_2_percent_discount,
                })
              }
            >
              <Checkbox checked={!!storeUpdate.sepa_2_percent_discount} />
              <CheckBoxLabel>
                {i18n.t('Invoices:sepa2PercentDiscount')}
              </CheckBoxLabel>
            </CheckBoxLabelContainer>
            <HelpPopover
              text={i18n.t('Invoices:sepa2PercentDiscountHelp')}
              id="sepa_2_percent_discount"
            />
          </div>
        </EditItem>

        <EditItem title={i18n.t('Invoices:dunningReminderBlockSection')}>
          {/* Dunning Reminder Block Status */}
          <DunningReminderBlockStatus
            blockType={data.store?.dunning_reminder_block_type}
            blockUntil={data.store?.dunning_reminder_block_until}
            blockReason={data.store?.dunning_reminder_block_reason}
            translationNamespace="Invoices"
            isBlocked={data.store?.is_dunning_reminder_stopped === true}
          />

          {/* Dunning Reminder Block Change */}
          <div className="input-group mb-3">
            <span className="input-group-text" id="basic-addon1">
              {i18n.t('Invoices:dunningReminderBlockType')}
            </span>
            <select
              className="form-control"
              name="dunning_reminder_block_type"
              value={storeUpdate.dunning_reminder_block_type || ''}
              onChange={(e) =>
                setStoreUpdate({
                  ...storeUpdate,
                  dunning_reminder_block_type: e.target
                    .value as DunningReminderBlockType,
                })
              }
            >
              <option value="">{i18n.t('Common:selectOption')}</option>
              <option value={DunningReminderBlockType.Permanently}>
                {i18n.t('Invoices:dunningReminderBlockTypePermanently')}
              </option>
              <option value={DunningReminderBlockType.Temporary}>
                {i18n.t('Invoices:dunningReminderBlockTypeTemporary')}
              </option>
            </select>
          </div>

          {storeUpdate.dunning_reminder_block_type ===
            DunningReminderBlockType.Temporary && (
            <Alert color="info" className="mb-3">
              {i18n.t('Invoices:dunningReminderBlockTemporaryInfo')}
            </Alert>
          )}

          <div className="input-group mb-3">
            <span className="input-group-text" id="basic-addon1">
              {i18n.t('Invoices:dunningReminderBlockReason')}
            </span>
            <input
              type="text"
              className={
                'form-control' +
                (storeUpdate.dunning_reminder_block_type ===
                  DunningReminderBlockType.Permanently &&
                isNullOrWhitespace(storeUpdate.dunning_reminder_block_reason)
                  ? ' is-invalid'
                  : '')
              }
              name="dunning_reminder_block_reason"
              autoComplete="off"
              onChange={handleChange}
              value={storeUpdate.dunning_reminder_block_reason || ''}
              placeholder={i18n.t('Invoices:dunningReminderBlockReasonHelp')}
            />
            <HelpPopover
              text={i18n.t('Invoices:dunningReminderBlockReasonHelp')}
              id="dunning_reminder_block_reason"
            />
          </div>

          <div className="input-group mb-3">
            <CheckBoxLabelContainer
              onClick={() => {
                if (!IsFinance(viewer)) {
                  return;
                }
                setStoreUpdate({
                  ...storeUpdate,
                  inkassokennzeichen: !storeUpdate.inkassokennzeichen,
                });
              }}
            >
              <Checkbox
                checked={!!storeUpdate.inkassokennzeichen}
                disabled={!IsFinance(viewer)}
              />
              <CheckBoxLabel>
                {i18n.t('Invoices:inkassokennzeichen')}
              </CheckBoxLabel>
            </CheckBoxLabelContainer>
            <HelpPopover
              text={i18n.t('Invoices:inkassokennzeichenHelp')}
              id="inkassokennzeichen"
            />
            {!IsFinance(viewer) && (
              <span className="text-muted ms-2">
                {i18n.t('Invoices:financeOnly')}
              </span>
            )}
          </div>
        </EditItem>

        <EditItem title={i18n.t('Invoices:invoicingStatusSection')}>
          <InvoicingPauseCard
            isCurrentlyDisabled={!!storeUpdate.is_invoicing_currently_disabled}
            disabledPermanently={!!storeUpdate.invoicing_disabled_permanently}
            disabledUntil={storeUpdate.invoicing_disabled_until || null}
            canEdit={IsFinance(viewer)}
            onTogglePermanent={() =>
              setStoreUpdate({
                ...storeUpdate,
                invoicing_disabled_permanently:
                  !storeUpdate.invoicing_disabled_permanently,
              })
            }
            onChangeUntil={handleChange}
          />
        </EditItem>

        <EditItem title={i18n.t('Invoices:contactLegalInformation')}>
          <div className="input-group mb-3">
            <span className="input-group-text" id="basic-addon1">
              {i18n.t('Invoices:externalId')}
            </span>
            <input
              type="text"
              className={'form-control'}
              name="external_id"
              autoComplete="off"
              lang="en-US"
              onChange={(e) => {
                handleChange(e);
              }}
              value={storeUpdate.external_id || ''}
              placeholder={''}
            />
          </div>
          <div className="input-group mb-3">
            <span className="input-group-text" id="basic-addon1">
              {i18n.t('Invoices:invoiceEmail')}
            </span>
            <input
              type="text"
              className={
                'form-control ' +
                (!isValidEmailInput(storeUpdate.invoice_email)
                  ? 'is-invalid'
                  : '')
              }
              name="invoice_email"
              autoComplete="off"
              lang="en-US"
              onChange={(e) => {
                handleChange(e);
              }}
              value={storeUpdate.invoice_email || ''}
              placeholder={'bob@example.com;alice@example.com'}
            />
          </div>
          <div className="input-group mb-1">
            <CheckBoxLabelContainer
              onClick={() => {
                const accountGates =
                  !!storeUpdate.purchasing_order_gating?.source_account_id;
                // While a partner account gates the store the checkbox mirrors
                // that state and cannot be switched off here — manage it on
                // the account.
                if (
                  accountGates &&
                  !storeUpdate.needs_purchasing_order_number
                ) {
                  return;
                }
                setStoreUpdate({
                  ...storeUpdate,
                  needs_purchasing_order_number:
                    !storeUpdate.needs_purchasing_order_number,
                  // the contact only exists to be asked for a PO, so it goes
                  // with the switch — unless a partner account keeps the store
                  // gated anyway
                  purchasing_order_email:
                    storeUpdate.needs_purchasing_order_number && !accountGates
                      ? null
                      : storeUpdate.purchasing_order_email,
                });
              }}
            >
              <Checkbox
                checked={
                  !!storeUpdate.needs_purchasing_order_number ||
                  !!storeUpdate.purchasing_order_gating?.source_account_id
                }
              ></Checkbox>
              <CheckBoxLabel>
                {i18n.t('Invoices:needsPurchasingOrderNumber')}
              </CheckBoxLabel>
            </CheckBoxLabelContainer>
            <HelpPopover
              text={i18n.t('Invoices:needsPurchasingOrderNumberHelp')}
              id="needs_purchasing_order_number"
            />
          </div>
          {!storeUpdate.needs_purchasing_order_number &&
            !!storeUpdate.purchasing_order_gating?.source_account_id && (
              <div className="form-text mb-2">
                {i18n.t('Invoices:purchasingOrderGatingInheritedFromAccount', {
                  account:
                    storeUpdate.purchasing_order_gating.source_account_name ||
                    storeUpdate.purchasing_order_gating.source_account_id,
                })}
                {!(storeUpdate.purchasing_order_email || '').trim() &&
                  storeUpdate.purchasing_order_gating
                    .purchasing_order_email && (
                    <>
                      {' '}
                      {i18n.t(
                        'Invoices:purchasingOrderGatingEffectiveContact',
                        {
                          contacts:
                            storeUpdate.purchasing_order_gating
                              .purchasing_order_email,
                        },
                      )}
                    </>
                  )}
              </div>
            )}
          <div className="input-group mb-3">
            <span className="input-group-text" id="basic-addon1">
              {i18n.t('Invoices:purchasingOrderEmail')}
            </span>
            <input
              type="text"
              className={
                'form-control ' +
                (!isValidPurchasingOrderEmailInput(
                  storeUpdate.purchasing_order_email,
                )
                  ? 'is-invalid'
                  : '')
              }
              name="purchasing_order_email"
              autoComplete="off"
              lang="en-US"
              disabled={
                !storeUpdate.needs_purchasing_order_number &&
                !storeUpdate.purchasing_order_gating?.source_account_id
              }
              onChange={(e) => {
                handleChange(e);
              }}
              value={storeUpdate.purchasing_order_email || ''}
              placeholder={'po1@example.com;po2@example.com'}
            />
            <HelpPopover
              text={i18n.t('Invoices:purchasingOrderEmailHelp')}
              id="purchasing_order_email"
            />
          </div>
          <div className="input-group mb-3">
            <span className="input-group-text" id="basic-addon1">
              {i18n.t('Invoices:legalName')}
            </span>
            <input
              type="text"
              className={'form-control'}
              name="legal_name"
              autoComplete="off"
              lang="en-US"
              onChange={(e) => {
                handleChange(e);
              }}
              value={storeUpdate.legal_name || ''}
              placeholder={''}
            />
          </div>
          <div className="input-group mb-3">
            <span className="input-group-text" id="basic-addon1">
              {i18n.t('Invoices:emailSubjectPrefixCapitalized')}
            </span>
            <input
              type="text"
              className={'form-control'}
              name="invoice_email_subject_prefix"
              autoComplete="off"
              lang="en-US"
              onChange={(e) => {
                handleChange(e);
              }}
              value={storeUpdate.invoice_email_subject_prefix || ''}
              placeholder={''}
            />
          </div>
        </EditItem>

        <EditItem title={i18n.t('Invoices:addressInformation')}>
          <div className="input-group mb-3">
            <span className="input-group-text" id="basic-addon1">
              {i18n.t('Common:street')}
            </span>
            <input
              type="text"
              className="form-control"
              name="invoice_address.streetname"
              autoComplete="off"
              onChange={handleChange}
              value={
                (storeUpdate.invoice_address &&
                  storeUpdate.invoice_address.streetname) ||
                ''
              }
            />
          </div>
          <div className="input-group mb-3">
            <span className="input-group-text" id="basic-addon1">
              {i18n.t('Invoices:streetNumber')}
            </span>
            <input
              type="text"
              className="form-control"
              onChange={handleChange}
              autoComplete="off"
              name="invoice_address.streetno"
              value={
                (storeUpdate.invoice_address &&
                  storeUpdate.invoice_address.streetno) ||
                ''
              }
            />
          </div>
          <div className="input-group mb-3">
            <span className="input-group-text" id="basic-addon1">
              {i18n.t('Common:streetExtra')}
            </span>
            <input
              type="text"
              className="form-control"
              onChange={handleChange}
              name="invoice_address.street_extra"
              value={
                (storeUpdate.invoice_address &&
                  storeUpdate.invoice_address.street_extra) ||
                ''
              }
            />
          </div>
          <div className="input-group mb-3">
            <span className="input-group-text" id="basic-addon1">
              {i18n.t('Common:zipCode')}
            </span>
            <input
              type="text"
              maxLength={10}
              className={
                'form-control ' +
                (!isValidPostalCode(
                  (storeUpdate.invoice_address || {}).zip,
                  (storeUpdate.invoice_address || {}).country,
                )
                  ? 'is-invalid'
                  : '')
              }
              autoComplete="off"
              name="invoice_address.zip"
              onChange={(e) => {
                // Normalize on every keystroke for real-time formatting
                const inputValue = e.target.value.substring(0, 10);
                const normalized = normalizePostalCode(
                  inputValue,
                  (storeUpdate.invoice_address || {}).country,
                );
                handleChange({
                  target: {
                    name: 'invoice_address.zip',
                    value: normalized,
                    hasAttribute: () => false, // zip codes are not numbers or dates
                  },
                });
              }}
              value={(storeUpdate.invoice_address || {}).zip || ''}
            />
          </div>
          <div className="input-group mb-3">
            <span className="input-group-text" id="basic-addon1">
              {i18n.t('Common:city')}
            </span>
            <input
              type="text"
              className="form-control"
              name="invoice_address.city"
              autoComplete="off"
              onChange={handleChange}
              value={storeUpdate.invoice_address?.city || ''}
              placeholder={i18n.t('Invoices:cityPlaceholder')}
            />
          </div>
          <div className="input-group mb-3">
            <span className="input-group-text" id="basic-addon1">
              {i18n.t('Common:country')}
            </span>

            <select
              className="form-control"
              name="invoice_address.country"
              autoComplete="off"
              onChange={handleChange}
              value={storeUpdate.invoice_address?.country || undefined}
              // @ts-ignore
              placeholder={i18n.t('Invoices:countryPlaceholder')}
            >
              {countries.map((country: string) => (
                <option key={country} value={country}>
                  {i18n.t(`Common:Country:${country}`) as string}
                </option>
              ))}
            </select>
          </div>
        </EditItem>

        <EditItem title={i18n.t('Invoices:bankingInformation')}>
          <div className="input-group mb-3">
            <div className="input-group-prepend">
              <span className="input-group-text" id="basic-addon1">
                {i18n.t('Invoices:ibanAccountHolder')}
              </span>
            </div>
            <input
              type="text"
              className={'form-control'}
              name="invoice_iban_name"
              autoComplete="off"
              lang="en-US"
              onChange={(e) => {
                handleChange(e);
              }}
              value={storeUpdate.invoice_iban_name || ''}
              placeholder={''}
            />
          </div>
          <div className="input-group mb-3">
            <span className="input-group-text" id="basic-addon1">
              {i18n.t('Invoices:iban')}
            </span>
            <input
              type="text"
              className={'form-control'}
              name="invoice_iban"
              autoComplete="off"
              lang="en-US"
              onChange={(e) => {
                handleChange(e);
              }}
              value={storeUpdate.invoice_iban || ''}
              placeholder={''}
            />
          </div>
        </EditItem>

        <EditItem title={i18n.t('Invoices:partnerCreditsSettings')}>
          <div className="input-group mb-3">
            <span className="input-group-text" id="basic-addon1">
              {i18n.t('Invoices:partnerCredits')} (
              {centsToPrice(storeUpdate.partner_credits, storeUpdate.currency)})
            </span>
            <input
              type="number"
              className={'form-control'}
              name="partner_credits"
              autoComplete="off"
              lang="en-US"
              onChange={handleChange}
              step="1"
              value={storeUpdate.partner_credits || ''}
              placeholder={''}
            />
            <HelpPopover
              text={i18n.t('Invoices:partnerCreditsHelp')}
              id="partner_credits"
            />
          </div>
          <div className="mb-3">
            <IconButton
              className="me-2"
              icon={<FaMoneyBill />}
              label={i18n.t('Invoices:getPartnerCreditInfo')}
              onClick={onGetAlreadyUsedPartnerCredits}
              isLoading={gettingUsedPartnerCredits}
            />
            {getUsedPartnerCreditsData && getUsedPartnerCreditsData.store && (
              <p className="mt-2">
                Result:{' '}
                {centsToPrice(
                  getUsedPartnerCreditsData.store.partner_credits,
                  storeUpdate.currency,
                )}{' '}
                (partner credits) -{' '}
                {centsToPrice(
                  getUsedPartnerCreditsData.getUsedPartnerCredits,
                  storeUpdate.currency,
                )}{' '}
                (already used credits) ={' '}
                {centsToPrice(
                  Math.max(
                    getUsedPartnerCreditsData.store.partner_credits -
                      getUsedPartnerCreditsData.getUsedPartnerCredits,
                    0,
                  ),
                  storeUpdate.currency,
                )}{' '}
                (credits left)
              </p>
            )}
          </div>
          <div className="input-group mb-3">
            <span className="input-group-text" id="basic-addon1">
              {i18n.t('Invoices:partnerCreditsExpireAfter')}
            </span>
            <input
              type="month"
              className="form-control"
              data-isdate
              name="partner_credits_usable_until"
              onChange={handleChange}
              value={
                storeUpdate.partner_credits_usable_until != null
                  ? DateTimeHelpers.toMonthInputString(
                      storeUpdate.partner_credits_usable_until,
                    )
                  : ''
              }
            />
            <HelpPopover
              text={i18n.t('Invoices:partnerCreditsExpireAfterHelp')}
              id="partner_credits_usable_until"
            />
          </div>
          <div className="input-group mb-3">
            <CheckBoxLabelContainer
              onClick={() =>
                setStoreUpdate({
                  ...storeUpdate,
                  disable_automatic_partner_charging:
                    !storeUpdate.disable_automatic_partner_charging,
                })
              }
            >
              <Checkbox
                checked={
                  storeUpdate.disable_automatic_partner_charging || false
                }
              ></Checkbox>
              <CheckBoxLabel>
                {i18n.t('Invoices:disableAutomaticPartnerCharging')}
              </CheckBoxLabel>
            </CheckBoxLabelContainer>
            <HelpPopover
              text={i18n.t('Invoices:disableAutomaticPartnerChargingHelp')}
              id="disable_automatic_partner_charging"
            />
          </div>
          <div className="input-group mb-3">
            <CheckBoxLabelContainer
              onClick={() =>
                setStoreUpdate({
                  ...storeUpdate,
                  disable_automatic_partner_invoice_sending:
                    !storeUpdate.disable_automatic_partner_invoice_sending,
                })
              }
            >
              <Checkbox
                checked={
                  storeUpdate.disable_automatic_partner_invoice_sending || false
                }
              ></Checkbox>
              <CheckBoxLabel>
                {i18n.t('Invoices:doNotSendInvoiceAutomatically')}
              </CheckBoxLabel>
            </CheckBoxLabelContainer>
            <HelpPopover
              text={i18n.t(
                'Invoices:disableAutomaticPartnerInvoiceSendingHelp',
              )}
              id="disable_automatic_partner_invoice_sending"
            />
          </div>
        </EditItem>

        <EditItem title={i18n.t('Invoices:customInvoiceItems')}>
          <p>{i18n.t('Invoices:assetTabNote')}</p>
          <div className="input-group mb-3">
            <table className="table-striped mb-0 table">
              <tbody>
                {(storeUpdate.custom_invoice_items || []).map(
                  (invoiceItem: any, itemIndex: number) => {
                    return (
                      <tr key={itemIndex}>
                        <td>
                          <div className="input-group mb-3">
                            <span
                              className="input-group-text"
                              id="basic-addon1"
                            >
                              {i18n.t('Common:type')}
                            </span>
                            <select
                              className="form-control"
                              autoComplete="off"
                              value={invoiceItem.type || 'DEFAULT'}
                              onChange={(e) =>
                                updateCustomInvoiceItem(itemIndex, {
                                  type: e.target.value,
                                })
                              }
                            >
                              {customInvoiceItemTypes}
                            </select>
                          </div>
                          <div className="input-group mb-3">
                            <span
                              className="input-group-text"
                              id="basic-addon1"
                            >
                              {i18n.t('Common:title')}
                            </span>
                            <input
                              type="text"
                              className="form-control"
                              autoComplete="off"
                              value={invoiceItem.title}
                              onChange={(e) =>
                                updateCustomInvoiceItem(itemIndex, {
                                  title: e.target.value,
                                })
                              }
                              placeholder={i18n.t('Invoices:titlePlaceholder')}
                            />
                          </div>
                          <div className="input-group mb-3">
                            <span
                              className="input-group-text"
                              id="basic-addon1"
                            >
                              {i18n.t('Common:description')}
                            </span>
                            <input
                              type="text"
                              className="form-control"
                              autoComplete="off"
                              value={invoiceItem.description}
                              onChange={(e) =>
                                updateCustomInvoiceItem(itemIndex, {
                                  description: e.target.value,
                                })
                              }
                              placeholder={i18n.t(
                                'Invoices:descriptionPlaceholder',
                              )}
                            />
                          </div>
                          <div className="input-group mb-3">
                            <span
                              className="input-group-text"
                              id="basic-addon1"
                            >
                              {i18n.t('Common:total')} (
                              {centsToPrice(
                                invoiceItem.total,
                                storeUpdate.currency,
                              )}
                              )
                            </span>
                            <input
                              type="number"
                              className="form-control"
                              autoComplete="off"
                              value={invoiceItem.total}
                              onChange={(e) =>
                                updateCustomInvoiceItem(itemIndex, {
                                  total: Number(e.target.value),
                                })
                              }
                              pattern="\d*"
                              step="1"
                              placeholder={i18n.t('Invoices:totalPlaceholder')}
                            />
                          </div>
                          <div className="input-group mb-3">
                            <span
                              className="input-group-text"
                              id="basic-addon1"
                            >
                              {i18n.t('Invoices:billingFrequency')}
                            </span>
                            <select
                              className="form-control"
                              autoComplete="off"
                              value={invoiceItem.billing_frequency || 'MONTHLY'}
                              onChange={(e) =>
                                updateCustomInvoiceItem(itemIndex, {
                                  billing_frequency: e.target.value,
                                })
                              }
                            >
                              {billingFrequencies}
                            </select>
                          </div>
                          <div className="input-group mb-3">
                            <p>
                              <b>{i18n.t('Invoices:pleaseNote')}:</b>{' '}
                              {i18n.t('Invoices:billingWithOnboardingNote')}
                            </p>
                            <CheckBoxLabelContainer
                              onClick={() =>
                                updateCustomInvoiceItem(itemIndex, {
                                  billing_start_with_onboarding:
                                    !invoiceItem.billing_start_with_onboarding,
                                })
                              }
                            >
                              <Checkbox
                                checked={
                                  invoiceItem.billing_start_with_onboarding
                                }
                              />
                              <CheckBoxLabel>
                                {i18n.t('Invoices:startBillingWithOnboarding')}
                              </CheckBoxLabel>
                            </CheckBoxLabelContainer>
                          </div>
                          <div
                            className="input-group mb-3"
                            style={{
                              opacity: invoiceItem.billing_start_with_onboarding
                                ? 0.5
                                : 1,
                            }}
                          >
                            <span
                              className="input-group-text"
                              id="basic-addon1"
                            >
                              {i18n.t('Invoices:startBillingInMonth')}
                            </span>
                            <input
                              type="month"
                              disabled={
                                invoiceItem.billing_start_with_onboarding
                              }
                              className="form-control"
                              placeholder="2022-01"
                              value={(
                                invoiceItem.billing_start || ''
                              ).substring(0, 7)}
                              onChange={(e) =>
                                updateCustomInvoiceItem(itemIndex, {
                                  billing_start: e.target.value,
                                })
                              }
                            />
                          </div>
                        </td>
                        <td>
                          <button
                            className="btn btn-danger"
                            onClick={() => deleteCustomInvoiceItem(itemIndex)}
                          >
                            {i18n.t('Common:remove')}
                          </button>
                        </td>
                      </tr>
                    );
                  },
                )}
              </tbody>
            </table>
            <IconButton
              className="mt-2"
              icon={<FaPlus />}
              label={i18n.t('Invoices:addNewInvoiceItem')}
              onClick={addNewCustomInvoiceItem}
            />
          </div>
        </EditItem>
        <InvoiceActions
          storeId={externalPickpackId}
          currency={store.currency}
        />
      </EditSection>
    </ContentSection>
  );
};
